import React, { useState, useEffect } from 'react';
import Modal from '../Modal.js';
import Button from '../Button.js';
import FormInput from '../FormInput.js';
import SelectField from '../SelectField.js';

const UserDialog = ({
  isOpen,
  onClose,
  onSave,
  user = null,
  branches = [],
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    branchId: ''
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || user.username || '',
        email: user.email || '',
        password: '',
        role: user.role || '',
        branchId: user.branchId || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: '',
        branchId: ''
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const roleRequiresBranch = formData.role && formData.role.toLowerCase() !== 'admin';

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!isEditing && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (roleRequiresBranch && !formData.branchId) {
      newErrors.branchId = 'Branch is required for non-admin users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    onSave(formData);
  };

  const roleOptions = [
    { label: 'Admin', value: 'admin' },
    { label: 'Manager', value: 'manager' },
    { label: 'Cashier', value: 'cashier' },
    { label: 'Staff', value: 'staff' }
  ];

  const branchOptions = branches.map(branch => ({
    label: branch.name,
    value: branch.id
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit User' : 'Create New User'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput
          id="name"
          name="name"
          label="Full Name"
          placeholder="Enter full name"
          value={formData.name}
          onChange={handleInputChange}
          error={errors.name}
          required
        />

        <FormInput
          id="email"
          name="email"
          label="Email Address"
          type="email"
          placeholder="user@example.com"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
          required
        />

        {!isEditing && (
          <FormInput
            id="password"
            name="password"
            label="Password"
            type="password"
            placeholder="Create a password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            required
          />
        )}

        {isEditing && (
          <FormInput
            id="password"
            name="password"
            label="New Password (leave empty to keep current)"
            type="password"
            placeholder="Enter new password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
          />
        )}

        <SelectField
          id="role"
          name="role"
          label="Role"
          options={roleOptions}
          value={formData.role}
          onChange={handleInputChange}
          error={errors.role}
          required
        />

        <SelectField
          id="branchId"
          name="branchId"
          label="Assign Branch"
          options={branchOptions}
          value={formData.branchId}
          onChange={handleInputChange}
          error={errors.branchId}
          required={roleRequiresBranch}
        />

        <Modal.Actions>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {isEditing ? 'Save Changes' : 'Create User'}
          </Button>
        </Modal.Actions>
      </form>
    </Modal>
  );
};

export default UserDialog;